export interface ColumnWidthConfig {
  readonly featureColumnPixels: number;
  readonly dataColumnPixels: number;
  readonly dataColumnMinPixels: number;
  readonly dataColumnMaxPixels?: number;
  readonly shouldScroll: boolean;
  readonly totalTableWidth: number;
  readonly visibleColumns: number; // How many data columns are visible at once
  readonly scrollableColumns: number; // Total number of data columns that can be scrolled through
}

export interface BreakpointConfig {
  mobile: boolean;
  tablet: boolean;
  laptop: boolean;
  xl: boolean;
}

export interface DimensionConstraints {
  minDataWidth: number;
  maxDataWidth?: number;
  maxFeatureWidth?: number;
}

export interface ResponsiveConfig {
  mobile: DimensionConstraints & {
    featureColumnPercentage: number;
  };
  tablet: DimensionConstraints & {
    featureColumnPercentage: number;
  };
  laptop: DimensionConstraints & {
    featureColumnPercentage: number;
    maxVisibleColumns: number;
  };
  xl: DimensionConstraints & {
    featureColumnPercentage: number;
    maxVisibleColumns?: number;
  };
}

export interface BreakpointThresholds {
  readonly mobile: number;
  readonly tablet: number;
  readonly laptop: number;
  readonly xl: number;
}

export type BreakpointName = keyof BreakpointThresholds;
export type ScrollMode = "fill" | "scroll";

export const BREAKPOINT_THRESHOLDS: BreakpointThresholds = {
  mobile: 480,
  tablet: 768,
  laptop: 1400,
  xl: 1401,
};

export const RESPONSIVE_CONFIG: ResponsiveConfig = {
  mobile: {
    featureColumnPercentage: 0.5,
    minDataWidth: 150,
  },
  tablet: {
    featureColumnPercentage: 1 / 3,
    minDataWidth: 180,
  },
  laptop: {
    featureColumnPercentage: 0.25,
    minDataWidth: 200,
    maxDataWidth: 250,
    maxVisibleColumns: 4,
  },
  xl: {
    featureColumnPercentage: 0.2,
    minDataWidth: 220,
    maxDataWidth: 260,
    maxFeatureWidth: 300,
  },
};

const getBreakpoint = (width: number): BreakpointConfig => {
  return {
    mobile: width <= BREAKPOINT_THRESHOLDS.mobile,
    tablet:
      width > BREAKPOINT_THRESHOLDS.mobile &&
      width <= BREAKPOINT_THRESHOLDS.tablet,
    laptop:
      width > BREAKPOINT_THRESHOLDS.tablet &&
      width <= BREAKPOINT_THRESHOLDS.laptop,
    xl: width > BREAKPOINT_THRESHOLDS.xl,
  };
};

const validateInputs = (containerWidth: number, columnCount: number): void => {
  if (containerWidth <= 0) {
    throw new Error(
      `Invalid container width: ${containerWidth}. Must be positive.`
    );
  }
  if (columnCount < 0) {
    throw new Error(
      `Invalid column count: ${columnCount}. Must be non-negative.`
    );
  }
  if (!Number.isInteger(columnCount)) {
    throw new Error(
      `Invalid column count: ${columnCount}. Must be an integer.`
    );
  }
};

const createZeroColumnConfig = (containerWidth: number, config: DimensionConstraints): ColumnWidthConfig => ({
  featureColumnPixels: containerWidth,
  dataColumnPixels: 0,
  dataColumnMinPixels: config.minDataWidth,
  dataColumnMaxPixels: config.maxDataWidth,
  shouldScroll: false,
  totalTableWidth: containerWidth,
  visibleColumns: 0,
  scrollableColumns: 0,
});

const calculateScrollConfig = (
  featureWidth: number,
  dataWidth: number,
  columnCount: number,
  containerWidth: number,
  visibleColumns: number
): ColumnWidthConfig => ({
  featureColumnPixels: featureWidth,
  dataColumnPixels: dataWidth,
  dataColumnMinPixels: dataWidth,
  shouldScroll: true,
  totalTableWidth: featureWidth + columnCount * dataWidth,
  visibleColumns: Math.max(1, Math.min(visibleColumns, columnCount)),
  scrollableColumns: columnCount,
});

const calculateFillConfig = (
  featureWidth: number,
  dataWidth: number,
  columnCount: number,
  containerWidth: number,
  config: DimensionConstraints
): ColumnWidthConfig => {
  const remainingWidth = containerWidth - featureWidth;
  const adjustedDataWidth = columnCount > 0 ? Math.floor(remainingWidth / columnCount) : 0;
  const finalDataWidth = Math.max(adjustedDataWidth, dataWidth);

  return {
    featureColumnPixels: featureWidth,
    dataColumnPixels: finalDataWidth,
    dataColumnMinPixels: config.minDataWidth,
    dataColumnMaxPixels: undefined,
    shouldScroll: false,
    totalTableWidth: containerWidth,
    visibleColumns: columnCount,
    scrollableColumns: columnCount,
  };
};

const calculateConstraintBasedConfig = (
  containerWidth: number,
  columnCount: number,
  config: DimensionConstraints & { featureColumnPercentage: number; maxVisibleColumns?: number },
  maxFeatureWidth?: number
): ColumnWidthConfig => {
  if (columnCount === 0) {
    return createZeroColumnConfig(containerWidth, config);
  }

  const percentageFeatureWidth = Math.floor(containerWidth * config.featureColumnPercentage);
  const featureWidth = maxFeatureWidth
    ? Math.min(percentageFeatureWidth, maxFeatureWidth)
    : percentageFeatureWidth;

  const remainingWidth = containerWidth - featureWidth;
  const idealDataWidth = Math.floor(remainingWidth / columnCount);

  let dataWidth: number;
  let shouldScroll = false;
  let visibleColumns = columnCount;

  if (idealDataWidth < config.minDataWidth) {
    dataWidth = config.minDataWidth;
    shouldScroll = true;
    visibleColumns = Math.floor(remainingWidth / dataWidth);
  } else if (config.maxVisibleColumns && columnCount > config.maxVisibleColumns) {
    if (idealDataWidth >= config.minDataWidth) {
      dataWidth = idealDataWidth;
    } else {
      dataWidth = config.minDataWidth;
      shouldScroll = true;
      visibleColumns = Math.floor(remainingWidth / dataWidth);
    }
  } else if (config.maxDataWidth && idealDataWidth > config.maxDataWidth) {
    dataWidth = config.maxDataWidth;
    const totalNeeded = featureWidth + (columnCount * dataWidth);

    if (totalNeeded <= containerWidth) {
      shouldScroll = false;
      visibleColumns = columnCount;
      dataWidth = Math.floor(remainingWidth / columnCount);
    } else {
      shouldScroll = true;
      visibleColumns = Math.floor(remainingWidth / dataWidth);
    }
  } else {
    dataWidth = idealDataWidth;
  }

  if (shouldScroll) {
    return calculateScrollConfig(featureWidth, dataWidth, columnCount, containerWidth, visibleColumns);
  } else {
    return calculateFillConfig(featureWidth, dataWidth, columnCount, containerWidth, config);
  }
};


export const calculateColumnWidths = (
  containerWidth: number,
  columnCount: number
): ColumnWidthConfig => {
  validateInputs(containerWidth, columnCount);

  const breakpoint = getBreakpoint(containerWidth);

  if (breakpoint.mobile) {
    const config = RESPONSIVE_CONFIG.mobile;

    if (columnCount === 0) {
      return createZeroColumnConfig(containerWidth, config);
    }

    const featureWidth = Math.floor(containerWidth * config.featureColumnPercentage);

    if (columnCount === 1) {
      return calculateFillConfig(
        featureWidth,
        containerWidth - featureWidth,
        1,
        containerWidth,
        config
      );
    }

    const dataWidth = Math.max(featureWidth, config.minDataWidth);
    return calculateScrollConfig(featureWidth, dataWidth, columnCount, containerWidth, 1);
  }

  if (breakpoint.tablet) {
    const config = RESPONSIVE_CONFIG.tablet;

    if (columnCount === 0) {
      return createZeroColumnConfig(containerWidth, config);
    }

    const featureWidth = Math.floor(containerWidth * config.featureColumnPercentage);

    if (columnCount === 1) {
      const singleColumnFeatureWidth = Math.floor(containerWidth * 0.5);
      return calculateFillConfig(
        singleColumnFeatureWidth,
        containerWidth - singleColumnFeatureWidth,
        1,
        containerWidth,
        config
      );
    }

    if (columnCount === 2) {
      const remainingWidth = containerWidth - featureWidth;
      const dataWidth = Math.floor(remainingWidth / 2);
      return calculateFillConfig(featureWidth, dataWidth, 2, containerWidth, config);
    }

    const scrollDataWidth = Math.max(featureWidth, config.minDataWidth);
    const availableWidth = containerWidth - featureWidth;
    const visibleCols = Math.floor(availableWidth / scrollDataWidth);

    return calculateScrollConfig(featureWidth, scrollDataWidth, columnCount, containerWidth, visibleCols);
  }

  if (breakpoint.laptop) {
    return calculateConstraintBasedConfig(containerWidth, columnCount, RESPONSIVE_CONFIG.laptop);
  }

  if (breakpoint.xl) {
    const config = RESPONSIVE_CONFIG.xl;
    return calculateConstraintBasedConfig(containerWidth, columnCount, config, config.maxFeatureWidth);
  }

  return calculateConstraintBasedConfig(containerWidth, columnCount, RESPONSIVE_CONFIG.laptop);
};
